/**
 * UserList Component
 * 
 * A comprehensive list view of users with features for:
 * - Displaying users in a table format
 * - Searching users by various fields
 * - Filtering users by role and phone
 * - Sorting users by different columns
 * - Pagination
 * - User management actions (view, edit, delete, change password)
 * 
 * The component includes:
 * - Search bar for finding users
 * - Filter dropdowns for role and phone
 * - Sortable table columns
 * - Action buttons for each user
 * - Pagination controls
 */

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { formatDateHKT, formatDateTimeHKT } from "@/utils/dateTimeHKT"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Eye, Edit, Trash2, MoreHorizontal, UserPlus, Key, Search, Upload } from "lucide-react"
import { UserStatus, UserRole, User } from "@/types/user-types"
import { PaginationControls } from "@/components/ui/pagination-controls"

interface UserListProps {
  /** Array of users to display */
  users: User[]
  /** Callback when search is performed */
  onSearch: (query: string, searchFields?: string[]) => Promise<void>
  /** Callback when filters are applied */
  onFilter: (filters: { role?: UserRole; status?: UserStatus | boolean; department?: string; phone?: string }) => Promise<void>
  /** Callback when a user is viewed */
  onView: (user: User) => void
  /** Callback when a user is edited */
  onEdit: (user: User) => void
  /** Callback when a user is deleted */
  onDelete: (user: User) => void
  /** Callback when adding a new user */
  onAddNew: () => void
  /** Callback for bulk upload */
  onBulkUpload?: () => void
  /** Callback when changing a user's password */
  onChangePassword: (user: User) => void
  /** Current page number */
  currentPage?: number
  /** Number of items per page */
  itemsPerPage?: number
  /** Callback when page changes */
  onPageChange?: (page: number) => void
  /** Callback when items per page changes */
  onItemsPerPageChange?: (count: number) => void
  /** Field to sort by */
  sortField?: string
  /** Sort direction */
  sortDirection?: "asc" | "desc"
  /** Callback when sort changes */
  onSort?: (field: string) => void
  /** Whether to show admin actions (create, edit, delete) */
  showAdminActions?: boolean
}

export function UserList({
  users,
  onSearch,
  onFilter,
  onView,
  onEdit,
  onDelete,
  onAddNew,
  onBulkUpload,
  onChangePassword,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  sortField = "username",
  sortDirection = "asc",
  onSort,
  showAdminActions = true,
}: UserListProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [roleFilter, setRoleFilter] = useState<UserRole | "">("")
  const [statusFilter, setStatusFilter] = useState<UserStatus | "">("")
  const [phoneFilter, setPhoneFilter] = useState("")
  const [searchFields] = useState<string[]>(["username", "firstName", "lastName", "email"])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch(searchQuery, searchFields)
  }

  const handleRoleFilterChange = (value: string) => {
    const role = value as UserRole | ""
    setRoleFilter(role)
    onFilter({ role: role || undefined, status: statusFilter || undefined })
  }

  const handleStatusFilterChange = (value: string) => {
    const status = value as UserStatus | ""
    setStatusFilter(status)
    onFilter({ role: roleFilter || undefined, status: status || undefined })
  }

  const handlePhoneFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value
    setPhoneFilter(phone)
    onFilter({ role: roleFilter || undefined, status: statusFilter || undefined, phone })
  }

  const clearFilters = () => {
    setRoleFilter("")
    setStatusFilter("")
    setPhoneFilter("")
    onFilter({})
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "Never"
    return formatDateHKT(date)
  }

  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return "Never"
    return formatDateTimeHKT(date)
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 hover:bg-red-200"
      case "staff":
        return "bg-blue-100 text-blue-800 hover:bg-blue-200"
      case "participant":
        return "bg-green-100 text-green-800 hover:bg-green-200"
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-200"
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 hover:bg-green-200"
      case "inactive":
        return "bg-gray-100 text-gray-800 hover:bg-gray-200"
      case "suspended":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-200"
    }
  }

  const getSortedPaginatedUsers = () => {
    // Apply sorting if enabled
    const sortedUsers = [...users]
    if (sortField && onSort) {
      sortedUsers.sort((a, b) => {
        // Handle different field types
        const aValue = a[sortField as keyof User]
        const bValue = b[sortField as keyof User]

        // Handle string comparison
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
        }

        // Handle date comparison
        if (sortField === "createdAt" || sortField === "lastLogin") {
          const aDate = aValue ? new Date(aValue as string).getTime() : 0
          const bDate = bValue ? new Date(bValue as string).getTime() : 0
          return sortDirection === "asc" ? aDate - bDate : bDate - aDate
        }

        // Default comparison
        const valA = aValue ?? 0
        const valB = bValue ?? 0
        return sortDirection === "asc" ? (valA > valB ? 1 : -1) : valA < valB ? 1 : -1
      })
    }

    // Apply pagination if enabled
    if (currentPage && itemsPerPage && onPageChange) {
      const startIndex = (currentPage - 1) * itemsPerPage
      return sortedUsers.slice(startIndex, startIndex + itemsPerPage)
    }

    return sortedUsers
  }

  const handleSort = (field: string) => {
    if (onSort) {
      const newDirection = field === sortField && sortDirection === "asc" ? "desc" : "asc"
      onSort(field)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 w-full">
          <div className="relative flex-1">
            <Input
              placeholder="Search by username, first/last name, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <Button type="submit">Search</Button>
        </form>
        <div className="flex items-center gap-2">
          {onBulkUpload && (
            <Button onClick={onBulkUpload}>
              <Upload className="mr-2 h-4 w-4" />
              Bulk Upload
            </Button>
          )}
          {showAdminActions && (
            <Button onClick={onAddNew}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add New User
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <p className="text-sm font-medium">Filter by Role</p>
          <Select value={roleFilter} onValueChange={handleRoleFilterChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Roles</SelectLabel>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="participant">Participant</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium">Filter by Phone</p>
          <Input
            placeholder="Enter phone number"
            value={phoneFilter}
            onChange={handlePhoneFilterChange}
            className="w-[180px]"
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium">Filter by Status</p>
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Status</SelectLabel>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {(roleFilter || statusFilter || phoneFilter) && (
          <Button variant="ghost" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className={sortField === "username" ? "cursor-pointer bg-gray-50" : "cursor-pointer"}
                onClick={() => handleSort("username")}
              >
                Username
                {sortField === "username" && <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>}
              </TableHead>
              <TableHead
                className={sortField === "phone" ? "cursor-pointer bg-gray-50" : "cursor-pointer"}
                onClick={() => handleSort("phone")}
              >
                Phone
                {sortField === "phone" && <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>}
              </TableHead>
              <TableHead
                className={sortField === "email" ? "cursor-pointer bg-gray-50" : "cursor-pointer"}
                onClick={() => handleSort("email")}
              >
                Email
                {sortField === "email" && <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>}
              </TableHead>
              <TableHead
                className={sortField === "role" ? "cursor-pointer bg-gray-50" : "cursor-pointer"}
                onClick={() => handleSort("role")}
              >
                Role
                {sortField === "role" && <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>}
              </TableHead>
              <TableHead
                className={sortField === "status" ? "cursor-pointer bg-gray-50" : "cursor-pointer"}
                onClick={() => handleSort("status")}
              >
                Status
                {sortField === "status" && <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>}
              </TableHead>
              <TableHead
                className={sortField === "lastLogin" ? "cursor-pointer bg-gray-50" : "cursor-pointer"}
                onClick={() => handleSort("lastLogin")}
              >
                Last Login
                {sortField === "lastLogin" && <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>}
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSortedPaginatedUsers().length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              getSortedPaginatedUsers().map((user) => (
                <TableRow key={user._id}>
                  <TableCell className="font-medium">{user.username}</TableCell>
                  <TableCell>{user.phoneNumber || "-"}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={getRoleBadgeColor(user.role)} variant="outline">
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusBadgeColor(user.isActive ? "active" : "inactive")} variant="outline">
                      {user.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDateTime(user.lastLogin)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onView(user)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        {showAdminActions && (
                          <DropdownMenuItem onClick={() => onEdit(user)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit User
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onChangePassword(user)}>
                          <Key className="mr-2 h-4 w-4" />
                          Change Password
                        </DropdownMenuItem>
                        {showAdminActions && (
                          <DropdownMenuItem className="text-red-600" onClick={() => onDelete(user)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {onPageChange && (
        <div className="flex justify-center">
          <PaginationControls
            currentPage={currentPage}
            totalItems={users.length}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={(newItemsPerPage) => {
              // Handle items per page change if needed
            }}
          />
        </div>
      )}
    </div>
  )
}
